import { useMutation } from '@tanstack/react-query';
import { has } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { CodPositions, COLLECTIONS } from 'src/constants/constants';
import { ILayoutConfigData, IViewChannel, SectionTypes } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';
import { getSingleScreenConfigurationLabel } from 'src/utils/screen-config';

import { advancedSettingsLayoutTags, getOrCreateLayoutTagId } from './helpers';
import { getOrCreateSectionId } from './useCreateScreenSection';

const sectionTypeMapping = {
  enableCustomerOrderDisplay: ['COD', 'Confirmation'],
  enableDynamicArea: ['DY', 'DYR'],
  enableCodTriggeredArea: ['DY', 'DYS'],
  enablePromos: ['DY', 'DYP'],
  enableThankYou: ['Thank You'],
  enableRotation: ['Section rotation'],
  enableTakeover: ['Take Over'],
};

type SectionTypeMappingKeys = keyof typeof sectionTypeMapping;

export interface ILayoutPayload
  extends Record<SectionTypeMappingKeys, boolean> {
  name: string;
  channel_name: string;
  layout_type_id?: number;
  codPosition?: CodPositions | null;
  screen_layout_type?: string;
  [key: `grid_${number}`]: string;
  [key: `placement_${number}`]: string;
  [key: `size_${number}`]: string;
  disclaimers: boolean[];
  selectedChannelInfo?: IViewChannel;
}

const getScreenId = async (orientation: string): Promise<string> => {
  const filters = [`name.regexp=placeholder${orientation}`];
  const query = generateQuery([], filters);

  const response = await requestCCApi({
    url: `/screens${query}`,
    method: 'GET',
  });

  if (response.data.length > 0) {
    return response.data?.[0]?.id;
  }

  const isVertical = orientation === 'Vertical';

  const defaultScreen = await requestCCApi({
    url: `/screens`,
    method: 'POST',
    data: {
      data: {
        name: `placeholder${orientation}`,
        section_type_id: 1,
        width: isVertical ? 1080 : 1920,
        height: isVertical ? 1920 : 1080,
      },
    },
  });

  await requestCCApi({
    url: `/screenSections`,
    method: 'POST',
    data: {
      data: {
        section_id: await getOrCreateSectionId({
          height: isVertical ? 1920 : 1080,
          width: isVertical ? 1080 : 1920,
        }),
        left_offset: 0,
        screen_id: defaultScreen.id,
        top_offset: 0,
      },
    },
  });

  return defaultScreen.id;
};

const getLayoutTagsFromSectionTypes = async (
  sectionTypes: string[],
  layoutTags: string[],
) => {
  const filteredSectionTypes = sectionTypes.filter((item) =>
    layoutTags.includes(item),
  );
  const layoutTagIds = [];

  for (const sectionType of filteredSectionTypes) {
    const layoutTagId = await getOrCreateLayoutTagId(sectionType);
    layoutTagIds.push(layoutTagId);
  }

  return layoutTagIds;
};

const generateScreenConfig = (payload: ILayoutPayload): string[] => {
  const screenConfig =
    payload.selectedChannelInfo?.screens?.map((screen: any, index: number) => {
      const grid = payload[`grid_${index}`]?.replace(/ /g, '').toLowerCase();
      const hasDisclaimer = payload.disclaimers[index];
      let disclaimerString = '';

      if (hasDisclaimer) {
        const placement = payload[`placement_${index}`];
        const size = payload[`size_${index}`];
        disclaimerString =
          placement && size ? `With${placement}${size}Disclaimer` : '';
      }

      const orientation =
        screen.height > screen.width ? 'vertical' : 'horizontal';

      return `${orientation}${grid}Grid${disclaimerString}`;
    }) || [];

  // For the right codPosition, it will need to reverse the screen configs to apply the disclaimer to the correct screen as the first screen will have disclaimer.
  return has(payload, 'codPosition') &&
    payload.codPosition === CodPositions.RIGHT
    ? [...screenConfig].reverse()
    : screenConfig;
};

const createTempLayout = async (payload: ILayoutPayload) => {
  const sectionTypes = new Set<string>();
  Object.entries(sectionTypeMapping).forEach(([key, types]) => {
    if (payload[key as SectionTypeMappingKeys])
      types.forEach((type) => sectionTypes.add(type));
  });

  const layoutTagIds = await getLayoutTagsFromSectionTypes(
    Array.from(sectionTypes),
    advancedSettingsLayoutTags,
  );
  const gridLines = generateScreenConfig(payload);

  const config: ILayoutConfigData = {
    gridLines,
    rotationGridLines: gridLines,
    sectionTypes: Array.from(sectionTypes) as SectionTypes[],
    ...(payload.screen_layout_type && {
      screenLayoutType: payload.screen_layout_type,
    }),
  };

  const response = await requestCCApi({
    url: `/layouts`,
    method: 'POST',
    data: {
      data: {
        name: payload.name,
        channel_name: payload.channel_name,
        layout_type_id: Number(payload.layout_type_id),
        config,
        layout_tag_id: layoutTagIds,
      },
    },
  });

  const layoutID = response.id;

  const [horizontalScreenId, verticalScreenId] = await Promise.all([
    getScreenId('Horizontal'),
    getScreenId('Vertical'),
  ]);

  const screens = payload.selectedChannelInfo?.screens || [];
  const transactionData = screens.map((screen: any, index: number) => {
    const orientation = getSingleScreenConfigurationLabel(screen);
    const screenId =
      orientation === 'Horizontal' ? horizontalScreenId : verticalScreenId;

    return {
      resource: COLLECTIONS.LAYOUT_SCREENS,
      method: 'POST',
      data: {
        layout_id: Number(layoutID),
        channel_screen_sequence: index + 1,
        screen_id: Number(screenId),
      },
    };
  });

  await requestCCApi({
    url: `/transaction`,
    method: 'POST',
    data: {
      data: transactionData,
    },
  });

  return response;
};

export const useCreateTempLayout = () => {
  return useMutation({
    mutationFn: createTempLayout,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.layouts.invalidateAll());
      queryClient.invalidateQueries(queryKeys.layoutScreens.invalidateAll());
    },
    onError: (error) => {
      console.error('Failed to create layout:', error);
    },
  });
};
